import jsPDF from "jspdf"
import { AllySettlement, AllySettlementStatus } from "../types/common.types"

// Liquidación de un aliado (Allies → aliado → Resumen → "Descargar PDF"): lo que el aliado le debe a
// la agencia por los tours del rango, cuánto ya pagó y cuánto falta para cerrarla. Sigue el diseño
// del estado de cuenta de Traspasos (utils/generateAccountStatementPdf.ts) para que los documentos que
// recibe una contraparte se vean de la misma familia: logo, resumen arriba, tabla, pie paginado.
// Se puede descargar siempre, también con todo pagado (David lo usa como comprobante).

type RGB = [number, number, number]

const C = {
    navy: [4, 44, 83] as RGB,
    ink: [24, 24, 24] as RGB,
    body: [70, 70, 70] as RGB,
    muted: [128, 126, 120] as RGB,
    faint: [170, 168, 160] as RGB,
    rule: [222, 220, 213] as RGB,
    band: [246, 246, 243] as RGB,
    boxBg: [251, 250, 247] as RGB,
    debtRed: [150, 30, 30] as RGB,
    creditGreen: [39, 109, 10] as RGB,
}

const STATUS: Record<AllySettlementStatus, { label: string, fg: RGB, bg: RGB }> = {
    pagado: { label: 'Pagado', fg: [43, 94, 24], bg: [226, 242, 222] },
    en_pago: { label: 'En pago', fg: [140, 84, 10], bg: [253, 238, 208] },
    pendiente: { label: 'Pendiente', fg: [150, 30, 30], bg: [252, 229, 229] },
}

const MESES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre']
const PAGE_MARGIN = 14
const FOOTER_H = 12
const ROW_H = 7

const money = (n: number) => `$${Math.round(Number(n) || 0).toLocaleString('es-CL')}`
const ddmmyyyy = (iso: string) => `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}`

// "Semana del 21 al 27 de septiembre 2026" / "Del 21 de septiembre al 4 de octubre 2026"
export function periodTitle(from: string, to: string) {
    const [fy, fm, fd] = from.split('-').map(Number)
    const [ty, tm, td] = to.split('-').map(Number)
    if (from === to) return `${fd} de ${MESES[fm - 1]} ${fy}`
    if (fy === ty && fm === tm) return `Semana del ${fd} al ${td} de ${MESES[tm - 1]} ${ty}`
    return `Del ${fd} de ${MESES[fm - 1]}${fy !== ty ? ` ${fy}` : ''} al ${td} de ${MESES[tm - 1]} ${ty}`
}

// Totales del documento. "Para cerrar" = lo que todavía no se cobró + lo que falta de lo cobrado.
export function settlementTotals(data: AllySettlement) {
    return {
        owed: data.totals.total,
        paid: data.totals.paid,
        toClose: data.totals.pending + data.totals.in_payment,
    }
}

class Pdf {
    doc: jsPDF
    family: string
    pageWidth: number
    pageHeight: number
    y = PAGE_MARGIN

    constructor(doc: jsPDF, family: string) {
        this.doc = doc
        this.family = family
        this.pageWidth = doc.internal.pageSize.getWidth()
        this.pageHeight = doc.internal.pageSize.getHeight()
    }
    get right() { return this.pageWidth - PAGE_MARGIN }
    get maxY() { return this.pageHeight - FOOTER_H - 4 }
    font(style: 'normal' | 'bold', size: number, color: RGB) {
        this.doc.setFont(this.family, style)
        this.doc.setFontSize(size)
        this.doc.setTextColor(color[0], color[1], color[2])
    }
    text(s: string, x: number, y: number, align: 'left' | 'right' | 'center' = 'left') {
        this.doc.text(s, x, y, { align })
    }
    fit(s: string, maxW: number) {
        if (this.doc.getTextWidth(s) <= maxW) return s
        let out = s
        while (out.length > 1 && this.doc.getTextWidth(out + '…') > maxW) out = out.slice(0, -1)
        return out.trimEnd() + '…'
    }
    rule(y: number, x1 = PAGE_MARGIN, x2 = this.right, color: RGB = C.rule, w = 0.2) {
        this.doc.setDrawColor(color[0], color[1], color[2])
        this.doc.setLineWidth(w)
        this.doc.line(x1, y, x2, y)
    }
    fill(x: number, y: number, w: number, h: number, color: RGB, r = 0) {
        this.doc.setFillColor(color[0], color[1], color[2])
        if (r > 0) this.doc.roundedRect(x, y, w, h, r, r, 'F')
        else this.doc.rect(x, y, w, h, 'F')
    }
}

// Columnas de la tabla (hoja horizontal, igual que los PDF que David armaba a mano: los nombres de
// tour son largos, "Geyser del Tatio, Vado de Putana y Machuca (Compartido)"). x de inicio para
// texto, borde derecho para montos.
const COL = {
    pax: PAGE_MARGIN + 2,
    date: PAGE_MARGIN + 64,
    tour: PAGE_MARGIN + 86,
    cost: PAGE_MARGIN + 196,
    ticket: PAGE_MARGIN + 218,
    total: PAGE_MARGIN + 240,
    status: PAGE_MARGIN + 244,
}

function drawTableHeader(p: Pdf) {
    p.fill(PAGE_MARGIN, p.y, p.right - PAGE_MARGIN, 7, C.band)
    p.font('bold', 7, C.muted)
    const base = p.y + 4.7
    p.text('PASAJERO', COL.pax, base)
    p.text('FECHA', COL.date, base)
    p.text('TOUR', COL.tour, base)
    p.text('COSTO', COL.cost, base, 'right')
    p.text('ENTRADA', COL.ticket, base, 'right')
    p.text('TOTAL', COL.total, base, 'right')
    p.text('ESTADO', COL.status, base)
    p.y += 7
}

export function buildAllySettlementPdf(data: AllySettlement, today: Date = new Date(), opts: { logo?: string } = {}): jsPDF {
    const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' })
    // Si el .ttf no se pudo descargar, jsPDF igual registra la familia y no lanza nada: el error
    // aparece recién al medir/escribir el primer texto ("reading 'widths'") y rompe el PDF entero.
    // Por eso se prueba a escribir con la fuente antes de usarla, y si falla se vuelve a helvetica.
    let family = 'Roboto'
    try {
        doc.addFont('/fonts/Roboto-Regular.ttf', 'Roboto', 'normal')
        doc.addFont('/fonts/Roboto-Bold.ttf', 'Roboto', 'bold')
        doc.setFont('Roboto', 'normal')
        doc.getTextWidth('Liquidación')
        doc.setFont('Roboto', 'bold')
        doc.getTextWidth('Liquidación')
    } catch (error) {
        family = 'helvetica'
    }
    doc.setFont(family, 'normal')
    const p = new Pdf(doc, family)
    const period = periodTitle(data.from, data.to)
    const generated = `Generado el ${today.getDate()} de ${MESES[today.getMonth()]} ${today.getFullYear()}`
    const totals = settlementTotals(data)

    // ── Cabecera ──
    try {
        // 'FAST' = comprimir: sin esto jsPDF guarda el PNG del logo (1081×1081) en crudo y una
        // liquidación de una página pesa ~4,6 MB.
        doc.addImage(opts.logo || '/assets/logo.png', 'PNG', PAGE_MARGIN, 9, 16, 16, undefined, 'FAST')
    } catch (error) {
        console.log('Could not load logo:', error)
    }
    p.font('bold', 12, C.navy)
    p.text('MOLANTUR', PAGE_MARGIN + 20, 15.5)
    p.font('normal', 8, C.navy)
    p.text('ATACAMA TOURS', PAGE_MARGIN + 20, 20)
    p.font('bold', 13, C.ink)
    p.text('Liquidación', p.right, 14.5, 'right')
    p.font('normal', 10, C.body)
    p.text(data.ally.name, p.right, 20, 'right')
    p.font('normal', 7.5, C.muted)
    p.text(`${period}  ·  ${generated}`, p.right, 25, 'right')
    p.y = 32
    p.rule(p.y)
    p.y += 6

    // ── Resumen: total adeudado, total pagado, para cerrar ──
    const boxW = (p.right - PAGE_MARGIN - 8) / 3
    const boxes: { label: string, value: number, color: RGB }[] = [
        { label: 'TOTAL ADEUDADO', value: totals.owed, color: C.ink },
        { label: 'TOTAL PAGADO', value: totals.paid, color: C.creditGreen },
        { label: 'PARA CERRAR', value: totals.toClose, color: totals.toClose > 0.5 ? C.debtRed : C.creditGreen },
    ]
    boxes.forEach((b, i) => {
        const x = PAGE_MARGIN + i * (boxW + 4)
        p.fill(x, p.y, boxW, 18, C.boxBg, 2)
        p.font('normal', 7, C.muted)
        p.text(b.label, x + 4, p.y + 6)
        p.font('bold', 14, b.color)
        p.text(money(b.value), x + 4, p.y + 14)
    })
    p.y += 24
    p.font('bold', 9, totals.toClose > 0.5 ? C.debtRed : C.creditGreen)
    p.text(
        totals.toClose > 0.5
            ? `Pago a realizar a ${data.agency_name}: ${money(totals.toClose)}`
            : 'Liquidación pagada completa. ¡Gracias!',
        PAGE_MARGIN, p.y
    )
    p.y += 7

    // ── Tabla de pasajeros ──
    drawTableHeader(p)
    data.rows.forEach((r, i) => {
        if (p.y + ROW_H > p.maxY) {
            doc.addPage()
            p.y = PAGE_MARGIN
            drawTableHeader(p)
        }
        if (i % 2 === 1) p.fill(PAGE_MARGIN, p.y, p.right - PAGE_MARGIN, ROW_H, C.boxBg)
        const base = p.y + 4.6
        p.font('normal', 7.8, C.ink)
        p.text(p.fit(r.passenger_name, COL.date - COL.pax - 3), COL.pax, base)
        p.font('normal', 7.8, C.body)
        p.text(ddmmyyyy(r.reservation_date), COL.date, base)
        p.text(p.fit(r.tour_id, COL.cost - COL.tour - 16), COL.tour, base)
        p.text(money(r.price), COL.cost, base, 'right')
        p.text(r.tickets > 0 ? money(r.tickets) : '—', COL.ticket, base, 'right')
        p.font('bold', 7.8, C.ink)
        p.text(money(r.total), COL.total, base, 'right')
        const st = STATUS[r.status]
        p.font('normal', 6.6, st.fg)
        const w = doc.getTextWidth(st.label) + 3
        p.fill(COL.status, base - 3, w, 4, st.bg, 1)
        p.text(st.label, COL.status + 1.5, base)
        p.y += ROW_H
    })
    if (data.rows.length === 0) {
        p.font('normal', 9, C.muted)
        p.text('No hay pasajeros con tour en este período.', p.pageWidth / 2, p.y + 8, 'center')
    }

    // ── Pie ──
    const pages = doc.getNumberOfPages()
    for (let i = 1; i <= pages; i++) {
        doc.setPage(i)
        p.rule(p.pageHeight - 9)
        p.font('normal', 6.8, C.faint)
        p.text(`${data.agency_name} · Liquidación ${data.ally.name} · ${period}`, PAGE_MARGIN, p.pageHeight - 5.5)
        p.text(`Generado por Traspax · página ${i} de ${pages}`, p.right, p.pageHeight - 5.5, 'right')
    }
    return doc
}

export default function downloadAllySettlementPdf(data: AllySettlement) {
    const doc = buildAllySettlementPdf(data)
    doc.save(`${data.ally.name}_${data.from}_al_${data.to}.pdf`.replace(/\s+/g, '_'))
}
